import { cn, getOrderSourceLabel, getOrderTypeLabel } from "@/lib/utils";
import { OrderSource, OrderType } from "@/redux/services/order/order.type";
import { computeDelta, Delta, DeltaText } from "./reportDelta";
import { reportCard } from "./reportCard";

interface OrderMixSectionProps {
  ordersByType: Record<string, number>;
  ordersBySource: Record<string, number>;
  previousOrdersByType?: Record<string, number>;
  previousOrdersBySource?: Record<string, number>;
}

interface Row {
  label: string;
  count: number;
  share: number;
  delta: Delta;
}

function toRows(
  entries: Record<string, number>,
  previous: Record<string, number> | undefined,
  labelOf: (key: string) => string
): Row[] {
  const sorted = Object.entries(entries).sort((a, b) => b[1] - a[1]);
  const total = sorted.reduce((sum, [, count]) => sum + count, 0);
  return sorted.map(([key, count]) => ({
    label: labelOf(key),
    count,
    share: total > 0 ? (count / total) * 100 : 0,
    delta: computeDelta(count, previous?.[key]),
  }));
}

function OrderMixSection({
  ordersByType,
  ordersBySource,
  previousOrdersByType,
  previousOrdersBySource,
}: OrderMixSectionProps) {
  const typeRows = toRows(ordersByType, previousOrdersByType, (k) =>
    getOrderTypeLabel(k as OrderType)
  );
  const sourceRows = toRows(ordersBySource, previousOrdersBySource, (k) =>
    getOrderSourceLabel(k as OrderSource)
  );

  if (typeRows.length === 0 && sourceRows.length === 0) {
    return null;
  }

  return (
    <section className={cn(reportCard, "p-6")}>
      <h2 className="mb-5 text-sm font-medium uppercase tracking-wide text-muted-foreground">
        Composition des commandes
      </h2>

      <div className="space-y-6">
        {typeRows.length > 0 && <BreakdownList label="Par type" rows={typeRows} />}
        {sourceRows.length > 0 && (
          <BreakdownList label="Par source" rows={sourceRows} />
        )}
      </div>
    </section>
  );
}

function BreakdownList({ label, rows }: { label: string; rows: Row[] }) {
  return (
    <div>
      <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <ul className="space-y-3">
        {rows.map((row) => (
          <li key={row.label}>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-card-foreground">{row.label}</span>
              <span className="flex items-center gap-2">
                <DeltaText delta={row.delta} className="text-xs" />
                <span className="tabular-nums font-semibold text-card-foreground">
                  {row.count}
                </span>
              </span>
            </div>
            {/* Proportion bar makes the mix legible at a glance. `primary` is a
                bare CSS var, so opacity modifiers (bg-primary/70) don't apply —
                use the solid colour, matching the payment-method bars. */}
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.min(row.share, 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default OrderMixSection;
